import { DemoApiMock } from '../types'
import { getDemoData, matchRestRequest, setDemoData } from '../utils'
import { matchPath } from 'react-router-dom'
import { WorkShift } from 'typings/common_defs'

interface RemoveWorkerPayload {
  propagate: boolean // if true, remove worker from the passed in shiftID and all shifts after it
  replace: boolean // if true, leave the spot unfilled. If false, reduce the number of workers by 1
  reason: string
  reason_category: string
  should_notify: boolean
}

export const mockAssignmentRemoveWorker: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      /\/company\/\d+\/assignment\/\d+\/worker\/\d+\/shift\/\d+/,
      'DELETE'
    ),
  getResponse: async (request) => {
    const match = matchPath(
      '/company/:companyId/assignment/:assignmentId/worker/:workerId/shift/:shiftId',
      request.url as string
    )
    const shiftId = Number(match?.params.shiftId)
    const workerId = Number(match?.params.workerId)
    const assignmentId = Number(match?.params.assignmentId)
    const payload = request.data as RemoveWorkerPayload
    const demoData = getDemoData()

    // Find the shift to remove the worker from
    const shift = demoData.shifts.find((s) => s.id === shiftId) as WorkShift
    if (!shift) {
      throw new Error('Shift not found')
    }

    // Get all shifts that need to be updated
    const shiftsToUpdate = payload.propagate
      ? demoData.shifts.filter(
          (s) => s.startsAt && shift.startsAt && s.startsAt >= shift.startsAt
        )
      : [shift]

    // Update each affected shift
    const updatedShifts = demoData.shifts.map((s) => {
      if (!shiftsToUpdate.includes(s)) {
        return s
      }

      const currentShift = s as WorkShift & {
        work?: Array<{ id: number; worker: { id: number; name: string } }>
      }
      const currentWork = currentShift.work || []

      // Remove the worker from the shift's work array
      const updatedWork = currentWork.filter((w) => w.worker?.id !== workerId)

      // Calculate new worker counts
      const newWorkersNeeded = payload.replace
        ? currentShift.workersNeeded ?? 0
        : Math.max(0, (currentShift.workersNeeded ?? 0) - 1)

      return {
        ...currentShift,
        work: updatedWork,
        workersNeeded: newWorkersNeeded,
        numWorkersFilled: updatedWork.length,
        numOpenSpots: payload.replace
          ? (currentShift.numOpenSpots ?? 0) + 1
          : currentShift.numOpenSpots ?? 0,
      }
    })

    setDemoData({
      ...demoData,
      shifts: updatedShifts,
      assignments:
        demoData.assignments?.map((assignment) => {
          if (assignment.id !== assignmentId) return assignment

          const assignmentShifts = updatedShifts.filter(
            (s) => s.assignment?.id === assignment.id
          )
          if (assignmentShifts.length === 0) return assignment

          return {
            ...assignment,
            listings: [
              {
                id: assignment.id,
                numWorkersPending: Math.max(
                  ...assignmentShifts
                    .filter((shift) => !shift.cancelledAt)
                    .map((shift) => shift.numOpenSpots ?? 0)
                ),
                shifts: assignmentShifts.map((shift) => ({
                  id: shift.id,
                  numOpenSpots: shift.numOpenSpots ?? 0,
                })),
              },
            ],
          }
        }) ?? [],
    })

    return {
      data: {
        success: true,
        message: 'Worker removed successfully',
        shifts: shiftsToUpdate.map((s) => ({
          id: s.id,
          workersNeeded: s.workersNeeded,
          numWorkersFilled:
            (
              s as WorkShift & {
                work?: Array<{
                  id: number
                  worker: { id: number; name: string }
                }>
              }
            ).work?.length ?? 0,
        })),
      },
    }
  },
}
